import validateCoupons from "@/api/requests/validateCoupons";
import Coupon from "@/types/Coupon";
import Event from "@/types/Event";
import Reservation from "@/types/Reservation";
import Sector from "@/types/Sector";
import TicketType from "@/types/TicketType";
import { computed, ComputedRef, ref, Ref } from "vue";

interface CouponUsage {
  coupon: Coupon;
  uses: number;
}

export function useCoupons(sectors: Ref<Sector[]>) {
  const list: Ref<Coupon[]> = ref([]);

  const applied: ComputedRef<Map<TicketType, CouponUsage[]>> = computed(() => {
    const map = new Map<TicketType, CouponUsage[]>();

    const types = sectors.value.flatMap(s => s.sections.flatMap(s => s.tickets)).sort(
      (a, b) => (b.current_batch?.price ?? 0) - (a.current_batch?.price ?? 0),
    );

    // Sort by order that they should be applied: absolute first and then percentage
    const _coupons = list.value.sort((a, b) => a.method.localeCompare(b.method));

    // Keeps track of max_items of each coupon
    const usesLeft = _coupons.reduce((acc, coupon) => ({ ...acc, [coupon.code]: coupon.max_items }), {}) as Record<string, number>;

    // Do the thing
    types.forEach((type) => {
      map.set(type, []);

      const coupons = _coupons.filter(coupon => usesLeft[coupon.code] > 0 && (coupon.ticket_types.length === 0 || coupon.ticket_types.some(t => t.id === type.id)));

      coupons.forEach((coupon) => {
        const remainder = usesLeft[coupon.code] - type.quantidade;
        const used = remainder >= 0 ? type.quantidade : (type.quantidade + remainder);
        map.get(type)?.push({ coupon: coupon, uses: used });
        usesLeft[coupon.code] = Math.max(0, remainder);
      });
    });

    return map;
  });

  async function validate(codes: string[], reservation: Reservation, event: Event): Promise<void> {
    if (codes.length === 0) return;

    const payload = Array.from(new Set([...codes, ...list.value.map(c => c.code)]));

    const { data } = await validateCoupons(payload, reservation.id, event.id);

    list.value = data;
  }

  function remove(coupon: Coupon): void {
    list.value = list.value.filter(c => c.id !== coupon.id);
  }

  function clear(): void {
    list.value = [];
  }

  function price(amount: number, coupon: Coupon): number {
    if (coupon.method === "percentage") {
      const percentage = (100 - coupon.value);

      return amount - (amount * percentage) / 100;
    }

    return amount - coupon.value / 100;
  }

  function discount(amount: number, coupon: Coupon): number {
    if (coupon.method === "percentage") {
      return (amount * Math.min(100, coupon.value)) / 100;
    }

    return Math.min(amount, coupon.value / 100);
  }

  return {
    list,
    applied,
    validate,
    remove,
    price,
    discount,
    clear,
  };
}
